/**
 * /dashboard/payroll/$id — Payroll run detail (G5.5).
 *
 * Data: GET /api/v1/payroll/runs/{id} returns the full payrollRunDTO
 * with embedded snapshot rows. The RunDetail component owns the
 * presentation; this route is the loading-state shell + role-gate.
 *
 * Backend visibility is HR-admin only for every /payroll/* endpoint
 * (no employee-self read in v1 — payslips are a separate G5.5 slice).
 * The PayrollRoleGate matches; backend remains the boundary.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/payroll/[id]/page.tsx with:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { payrollApi, toApiError } from "@/lib/api";
import { payrollKeys, usePayrollRun } from "@/hooks/use-payroll";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { RunDetail } from "@/components/payroll/run-detail";
import { Button } from "@/components/ui/button";
import { GetHelpButton } from "@/components/dashboard/get-help-button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatPeriod, type PayrollRunType } from "@/types/payroll";

/** "Payroll — October 2026", "Payroll — October 2026, Correction 1". */
function runTitle(run: {
  period_year: number;
  period_month: number;
  run_type?: PayrollRunType;
  correction_number?: number;
}): string {
  const period = formatPeriod(run.period_year, run.period_month);
  if (run.run_type !== "supplementary") return `Payroll — ${period}`;
  return `Payroll — ${period}, Correction${run.correction_number ? ` ${run.correction_number}` : ""}`;
}

export const Route = createFileRoute("/_authgated/app/$tenantSlug/payroll/$id")({
  // A9 hover-prefetch. Primary = the run detail read, keyed by the path
  // param exactly like usePayrollRun. Malformed ids skip the fetch (the
  // component renders its own "not found" state); errors are caught so
  // the component's useQuery error path stays the authority.
  loader: ({ context, params }) => {
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) return;
    return context.queryClient
      .ensureQueryData({
        queryKey: payrollKeys.run(id),
        queryFn: ({ signal }) => payrollApi.getRun(id, signal),
      })
      .catch(() => undefined);
  },
  component: PayrollRunDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-32 w-full" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
      <Skeleton className="h-96 w-full" />
    </div>
  );
}

function PayrollRunDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = usePayrollRun(numId, validId);

  return (
    <PayrollModuleShell
      // The run's name, so the breadcrumb and the tab title read the
      // record ("Payroll — October 2026").
      title={q.data ? runTitle(q.data) : "Payroll Run"}
      actions={
        <div className="flex flex-wrap gap-2">
          <GetHelpButton
            details={
              q.data
                ? [
                    `Payroll run: ${formatPeriod(q.data.period_year, q.data.period_month)} (${q.data.locked ? "locked" : "draft"})`,
                  ]
                : undefined
            }
          />
          {/* Phones only: the desktop breadcrumb (Payroll › …) goes back. */}
          <Button asChild variant="outline" className="gap-1.5 lg:hidden">
            <Link to="/app/$tenantSlug/payroll" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back
            </Link>
          </Button>
        </div>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid run id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid payroll run id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this run</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/payroll" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to Payroll
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && (
        // Keyed by run id: RunDetail seeds local state from initialRun, so
        // navigating to another run (e.g. the correction just created) must
        // remount it (2026-10 browser QA payroll #4).
        <RunDetail key={q.data.id} initialRun={q.data} tenantSlug={tenantSlug} />
      )}
    </PayrollModuleShell>
  );
}
